<script setup>
import MyJackpotBet from './MyJackpotBet.vue'
import SelectField from '@/components/Form/SelectField.vue'
import DefaultButton from '@/components/Buttons/DefaultButton.vue'
import { useAuthStore } from '@/stores/auth'
import { useJackpotStore } from '@/stores/jackpot'
import { storeToRefs } from 'pinia'
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { toFixedTwo } from '@/helpers'

const { t } = useI18n()
const auth = useAuthStore()
const jackpotStore = useJackpotStore()
const route = useRoute()
const tabs = [
  t('jackpots.8'), // Current
  t('jackpots.9'), // Previous
]
const { getCurrencies, getCurrency, getUser } = storeToRefs(auth)
const {
  getTickets,
  getTicketsUsers,
  getLastDailyJackpots,
  getLastWeeklyJackpots
} = storeToRefs(jackpotStore)

const activeTab = ref(0)      // 0 = current | 1 = previous
const daily = ref('1')        // '1' = daily | '0' = weekly
const currency = ref('')
const userId = ref(getUser.value._id)

const jackpotId = computed(() => {
  const list =
    daily.value === '1'
      ? getLastDailyJackpots.value
      : getLastWeeklyJackpots.value

  if (!Array.isArray(list) || list.length === 0) return ''

  return list[activeTab.value]?._id || ''
})
const fetchTickets = () => {
  if (!currency.value || !jackpotId.value) return

  jackpotStore.fetchJackpotTickets({
    daily: daily.value,
    currency: currency.value,
    jackpotId: jackpotId.value,
    ...(userId.value && { userId: userId.value }),
  })
}
watch(
  () => route.query.uid,
  (uid) => {
    userId.value = uid ?? getUser.value?._id
    fetchTickets()
  },
  { immediate: true }
)
watch(daily, () => {
  activeTab.value = 0
  fetchTickets()
})

watch(activeTab, fetchTickets)
watch(currency, fetchTickets)

onMounted(async () => {
  await Promise.all([
    jackpotStore.fetchLastDailyJackpots(),
    jackpotStore.fetchLastWeeklyJackpots(),
  ])

  currency.value = getCurrencies.value?.[0]?._id || ''
})
const applyFilters = () => {
  fetchTickets()
}
const setActiveTab = (tab) => {
  activeTab.value = tab
}
const selectUser = (id) => {
  userId.value = id
  fetchTickets()
}
</script>

<template>
  <div>
    <form @submit.prevent>
      <div class="px-4 py-2 flex flex-col md:flex-row md:gap-2">
        <SelectField
          :options="[
              {name: 'Weekly', code: '0'},
              {name: 'Daily', code: '1'},
          ]"
          value-attribute="code"
          text-attribute="name"
          v-model="daily"
          class="w-full rounded-t-none border-t border-neutral-200"
        />
        <SelectField
          v-model="currency"
          :options="getCurrencies"
          text-attribute="cod"
          value-attribute="_id"
          class="w-full rounded-t-none border-t border-neutral-200"
        />
        <DefaultButton
          @click.prevent="applyFilters"
          class="w-full md:w-auto"
        >
          {{ $t('general.26') }}
        </DefaultButton>
      </div>
    </form>
    <template v-if="!getTicketsUsers?.length">
      <nav class="z-20 border-t border-b border-white/10 overflow-x-auto hide-scrollbar">
        <ul class="flex items-center space-x-2 px-3 py-2">
          <li
            v-for="tab, i in tabs"
            :key="i"
            class="flex-shrink-0 cursor-pointer px-4 py-2 rounded-full text-sm transition-all duration-200 whitespace-nowrap"
            :class="[
                activeTab === i
                ? 'bg-white text-black font-semibold'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            ]"
            @click="setActiveTab(i)"
          >
            <span>{{ tab }}</span>
          </li>
        </ul>
      </nav>
      <div class="px-4 py-2 space-y-4">
        <template v-if="getTickets?.length">
          <ul class="space-y-2">
            <li v-for="ticket in getTickets" :key="ticket._id">
              <MyJackpotBet
                :ticket="ticket"
              />
            </li>
          </ul>
        </template>
        <div class="no-Tickets" v-else>
          <p class="text-sm font-bold text-neutral-300 text-center">
            {{ $t('jackpots.11') }}
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="px-4 py-2">
        <ul class="list-none space-y-2">
          <li
            v-for="user in getTicketsUsers"
            :key="user._id"
            class="text-neutral-300"
          >
            <div
              class="flex justify-between no-Tickets px-4 cursor-pointer"
              @click="selectUser(user._id)"
            >
              <p class="text-sm font-bold">
                <span>{{ user.un }}</span>
                <span class="ml-1 text-xs font-normal">({{ user.tnr }})</span>
              </p>
              <p class="text-xs">
                {{ toFixedTwo(user.am) }} {{ getCurrency(currency)?.cod }}
              </p>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
<style>
.tab-item {
  @apply inline-flex items-center px-2 py-1 rounded-2xl cursor-pointer;
}

.tab-item span {
  @apply text-white text-sm;
}

.tab-item.active {
  background-color: var(--color-primary3);
}

.tab-item.active span {
  color: #000;
  font-weight: 700;
}
.no-Tickets {
  background-image: var(--mgc-bg);
  @apply rounded-md overflow-hidden py-4;
}
</style>
